import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useState,
} from 'react'

import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { F0Icon, F0Text } from '@factorialco/f0-react'
import { PalmTree } from '@factorialco/f0-react/icons/app'
import moment from 'moment'

import { i18nDateToString } from 'modules/core/lib/dates'
import LeaveModal from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/ShiftCell/LeaveModal'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { trackEditTimeOffFromShiftStartEvent } from 'modules/shift_management/lib/trackEvents'
import type { LeaveGridEvent } from 'modules/shift_management/utils/TimeGridTable/transformEvents'
import AddShiftFromLeaveContextualButton from 'modules/shift_management/components/ShiftManagement/AddShiftFromLeaveContextualButton'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'

import type { EventCardSpec } from 'modules/shift_management/utils/TimeGridTable/resolveCard'

const LeaveCardContent = ({ event }: { event: LeaveGridEvent }) => {
  const [isOpen, setIsOpen] = useState(false)
  const dispatchSelection = useSelectionDispatch()
  const [{ view }] = useSchedule()
  const [{ shiftsRows }] = useShiftsRows()
  const shiftsRow = shiftsRows.find((row) => row.key === event.entityId)
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [isHovering, toggleHovering] = useToggle(false)
  const hasBackupShifts = hasFeatureFlag('shift_management_backup_shifts')
  const canManage = !!shiftsRow?.canManage
  const canAddBackupShifts = event.leave.canHaveBackupShifts

  const startDate = moment(event.start)
  const endDateInclusive = moment(event.end).subtract(1, 'day')
  const startLabel = i18nDateToString(startDate, 'ui_short')
  const endLabel = i18nDateToString(endDateInclusive, 'ui_short')
  const canOpenLeaveModal = event.selectable && !!shiftsRow
  const rangeLabel = startDate.isSame(endDateInclusive, 'day')
    ? startLabel
    : `${startLabel} → ${endLabel}`

  const openLeaveModal = () => {
    if (!canOpenLeaveModal || !shiftsRow) return

    dispatchSelection({ type: 'TOGGLE_LEAVE', leave: event.leave })
    trackEditTimeOffFromShiftStartEvent({ view, isApproved: !!event.leave.approved })
    setIsOpen(true)
  }

  const onCardClick = (clickEvent: ReactMouseEvent<HTMLDivElement>) => {
    clickEvent.stopPropagation()
    openLeaveModal()
  }

  const onCardKeyDown = (keyboardEvent: ReactKeyboardEvent<HTMLDivElement>) => {
    if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return

    keyboardEvent.preventDefault()
    keyboardEvent.stopPropagation()
    openLeaveModal()
  }

  const onClose = () => {
    setIsOpen(false)
    dispatchSelection({ type: 'CLEAR_SELECTION' })
  }

  return (
    <>
      <Box
        flexDirection="column"
        gap="s4"
        paddingBottom="s6"
        role={canOpenLeaveModal ? 'button' : undefined}
        tabIndex={canOpenLeaveModal ? 0 : undefined}
        aria-disabled={canOpenLeaveModal ? undefined : true}
        extra={
          canOpenLeaveModal
            ? {
                onClick: onCardClick,
                onKeyDown: onCardKeyDown,
              }
            : undefined
        }
        onMouseEnter={toggleHovering(true)}
        onMouseLeave={toggleHovering(false)}
        style={{ cursor: canOpenLeaveModal ? 'pointer' : 'default' }}
      >
        <Box flexDirection="row" alignItems="center" gap="s4">
          <F0Icon icon={PalmTree} size="sm" color="secondary" />
          <F0Text
            variant="label"
            content={event.leave.halfDay ? `½ ${event.leave.typeName}` : event.leave.typeName}
            ellipsis
          />
        </Box>
        <Box paddingLeft="s4">
          <F0Text variant="label" content={rangeLabel} ellipsis />
        </Box>
        {hasBackupShifts && shiftsRow && canManage && canAddBackupShifts && (
          <AddShiftFromLeaveContextualButton
            shiftsRow={shiftsRow}
            leave={event.leave}
            isBackup={canAddBackupShifts}
            date={startDate}
            isHoveringLeave={isHovering}
            toggleHovering={toggleHovering}
          />
        )}
      </Box>
      {isOpen && shiftsRow ? (
        <LeaveModal leave={event.leave} onClose={onClose} shiftsRow={shiftsRow} />
      ) : null}
    </>
  )
}

const LeaveCard: EventCardSpec<LeaveGridEvent> = {
  getAppearance: (event) => ({
    background: event.leave.approved ? 'champagne200' : 'grey200',
    opacity: '1',
    border: undefined,
    fillHeight: true,
  }),
  render: (event) => <LeaveCardContent event={event} />,
}

export { LeaveCard }
